import {
  statutMiciFiable,
  statutsMiciFiables,
  toutesMicisEnRemission,
} from './digestifSelection.js';

// ─── Résolution clinique — SOURCE UNIQUE ────────────────────────────────
//
// Créé le 2026-07-31 sur les règles validées par Anissa
// (docs/MATRICE-CHAMPS-CLINIQUES-2026-07-31.md).
//
// Ce module résout l'état clinique d'une cliente à partir de ses champs
// STRUCTURÉS, avec le texte libre en simple filet de sécurité. Il est la
// source unique consommée par les trois chemins de génération (éditeur de
// parcours, consultation, transition de phase automatique) : c'est ce qui
// garantit qu'aucun chemin ne puisse diverger, ni contourner un blocage.
//
// LES RÈGLES, telles qu'elle les a posées :
//
//  1. Une clé ABSENTE vaut `non_renseigne`. JAMAIS « aucun diagnostic ».
//     « Aucun » est une réponse explicite, distincte de l'absence.
//  2. Le champ structuré est PRIORITAIRE. Le texte libre ne remplace jamais
//     une réponse structurée positive — il ne sert qu'à signaler qu'une
//     vérification est nécessaire quand rien n'est renseigné.
//  3. Aucun diagnostic ni statut n'est DÉDUIT du texte. Le filet dit
//     « il y a peut-être quelque chose ici », jamais « c'est un diabète ».
//  4. Aucune donnée existante n'est marquée « vérifiée » automatiquement.
//
// Ce module NE BLOQUE RIEN : il décrit. Les blocages viendront ensuite et
// s'appuieront sur lui.

import { resolveMaternalStateExtended } from '../prompts/nutrition/profiles/_detector.fr.js';
import { readCurrentTreatments } from './currentTreatments.js';

/** Valeur d'un domaine jamais renseigné (clé absente ou vide). */
export const NON_RENSEIGNE = 'non_renseigne';

/** D'où vient l'information résolue. */
export const SOURCE = Object.freeze({
  STRUCTURE: 'structure', // la cliente a répondu à la question
  ABSENT: 'absent',       // rien de structuré
});

const lc = (v) => String(v ?? '').trim().toLowerCase();

/**
 * Diagnostics digestifs qui ouvrent la question du statut (MICI).
 * Exporté : le panneau praticienne et les tests de cohérence s'y adossent,
 * pour qu'une option ajoutée au questionnaire ne puisse pas passer à côté.
 */
export const MICI_VALEURS = Object.freeze(['crohn', 'rch', 'autremici']);

/** Diagnostics thyroïdiens qui ouvrent la question de la stabilisation. */
export const THYRO_A_STABILISER = Object.freeze(['hyperthyroidie', 'basedow']);

/** Réponse explicite « aucun diagnostic ». Distincte de l'absence de réponse. */
const AUCUN = 'aucun';

/**
 * Réponses qui ne DÉSIGNENT aucune situation clinique.
 *
 * Arbitrage 2 d'Anissa (2026-08-01) : « Autre », « je ne sais pas » ou une
 * information uniquement détectée dans le texte libre entraînent une
 * validation obligatoire. Aucun sous-module ne s'active automatiquement.
 */
const REPONSES_INCERTAINES = Object.freeze(['autre', 'jenesaispas']);

/**
 * Diagnostics diabétiques qui ouvrent l'axe glycémique (arbitrage 6).
 * L'insulinorésistance diagnostiquée est une réponse À PART ENTIÈRE : Anissa
 * a explicitement refusé qu'elle soit assimilée au prédiabète.
 */
export const DIABETE_AXE_GLYCEMIQUE = Object.freeze([
  'type2', 'prediabete', 'insulinoresistance',
]);

/**
 * Normalise une réponse en liste, qu'elle arrive en tableau (nouveau format
 * à réponses multiples) ou en chaîne (fiches créées avant le 2026-08-01).
 * La migration est douce : aucune fiche existante n'a besoin d'être réécrite.
 */
function normaliserListe(brut) {
  const liste = Array.isArray(brut) ? brut : (brut === null || brut === undefined || brut === '' ? [] : [brut]);
  return [...new Set(liste.map(lc).filter(Boolean))];
}

/**
 * Filets texte — servent UNIQUEMENT à déclencher une vérification humaine
 * quand le champ structuré est absent. Ils ne posent aucun diagnostic.
 */
const FILETS_TEXTE = Object.freeze({
  diabete: /diabet|diabète|insulinoresist|insulinorésist|prediabet|prédiabet|glycemie|glycémie/i,
  digestif: /\bsii\b|\bmici\b|crohn|rectocolite|\brch\b|colopathie|intestin irritable|reflux|\brgo\b/i,
  thyroide: /thyro[iï]d|hashimoto|basedow|levothyrox|hypothyr|hyperthyr/i,
});

/** Champs texte libre balayés par les filets (source unique). */
/**
 * Le texte libre qui parle de LA CLIENTE.
 *
 * ⚠️ `antecedentsFamiliaux` N'EST PAS DEDANS, et c'est une décision, pas un
 * oubli. Décision d'Anissa du 2026-08-03 : un antécédent familial décrit la
 * maladie d'un parent, pas celle de la cliente. Le confondre avec un
 * diagnostic bloquait 4 fiches sur 12 — « papa hypertension diabète » suffisait
 * à empêcher toute génération de plan, sans rien apprendre à personne.
 *
 * Il est lu à part, par `collecteAntecedentsFamiliaux`, et ce qu'on en tire ne
 * bloque rien.
 */
function collecteTexteLibre(form) {
  const f = form || {};
  return [
    f.pathologies, readCurrentTreatments(f).text,
    f.objectifPrincipalNutrition, f.objectifPrincipal, f.objectif_primaire,
    f.commentaires, f.commentaire, f.notes, f.symptomes,
  ].filter(Boolean).map(lc).join(' | ');
}

/** Le champ des antécédents familiaux, et lui seul. */
function collecteAntecedentsFamiliaux(form) {
  return lc((form || {}).antecedentsFamiliaux || '');
}

/**
 * Ce que les antécédents familiaux évoquent — en SIGNAL, jamais en blocage.
 *
 * Décision d'Anissa du 2026-08-03, mot pour mot : « un antécédent familial ne
 * doit pas être confondu avec une maladie diagnostiquée chez la cliente : il
 * ne doit pas empêcher la génération du plan. Mais l'information reste
 * pertinente et ne doit pas disparaître du radar. »
 *
 * D'où ce TROISIÈME ÉTAT, que le code ne connaissait pas : ni bloquant, ni
 * silencieux. Le résultat n'entre ni dans `verificationRequise`, ni dans les
 * blocages, ni dans l'activation d'un module — trois interdictions tenues par
 * des tests, dont un test structurel.
 *
 * Le texte d'origine n'est jamais réécrit : on le lit, on ne le touche pas.
 */
export function signauxAntecedentsFamiliaux(form) {
  const texte = collecteAntecedentsFamiliaux(form);
  const domaines = [];
  if (!texte) return { domaines, present: false };

  for (const [cle, filet] of Object.entries(FILETS_TEXTE)) {
    if (filet.test(texte)) domaines.push(cle);
  }
  return { domaines, present: domaines.length > 0 };
}

/**
 * Résout un domaine : structuré s'il existe, sinon `non_renseigne` — et on
 * note séparément si le texte libre évoque quelque chose.
 */
function resoudreDomaine(valeurStructuree, filet, texte) {
  const v = lc(valeurStructuree);
  const signalTexte = filet.test(texte);
  if (v) {
    // Réponse structurée présente : elle gagne, quoi qu'il y ait dans le
    // texte. Une cliente qui a répondu « aucun diagnostic » n'est PAS
    // requalifiée parce qu'un mot traîne dans ses commentaires.
    return { valeur: v, source: SOURCE.STRUCTURE, signalTexte, verificationRequise: false };
  }
  // Rien de structuré : non renseigné. Si le texte évoque le domaine, une
  // vérification humaine est requise — sans rien conclure.
  return {
    valeur: NON_RENSEIGNE,
    source: SOURCE.ABSENT,
    signalTexte,
    verificationRequise: signalTexte,
  };
}

/**
 * Même règle, pour un domaine à RÉPONSES MULTIPLES (digestif depuis
 * l'arbitrage 4 d'Anissa).
 *
 * `valeurs` porte la vérité. `valeur` reste exposée pour les lectures
 * existantes, mais ne doit plus servir à décider quoi que ce soit : une
 * cliente peut déclarer un SII ET un RGO, et un test qui ne regarderait que
 * `valeur` en manquerait un.
 */
function resoudreDomaineMulti(brut, filet, texte) {
  const valeurs = normaliserListe(brut);
  const signalTexte = filet.test(texte);

  if (valeurs.length === 0) {
    return {
      valeurs: [], valeur: NON_RENSEIGNE, source: SOURCE.ABSENT,
      signalTexte, verificationRequise: signalTexte,
    };
  }

  // « Aucun » coché avec un diagnostic réel est contradictoire. On garde le
  // diagnostic, jamais l'inverse : en cas d'incohérence, on ne perd pas une
  // pathologie déclarée.
  const reels = valeurs.filter((v) => v !== AUCUN);
  const retenues = reels.length > 0 ? reels : valeurs;

  return {
    valeurs: retenues,
    valeur: retenues[0],
    source: SOURCE.STRUCTURE,
    signalTexte,
    verificationRequise: false,
  };
}

/**
 * État clinique complet d'une cliente.
 *
 * @param {object} form - clients.form
 * @returns {{
 *   diabete: object, digestif: object, thyroide: object,
 *   traitements: object, verificationRequise: string[]
 * }}
 */
export function resolveClinicalState(form) {
  const f = form || {};
  const texte = collecteTexteLibre(f);
  // Signal, jamais blocage. Il voyage à part dans l'état pour que personne ne
  // puisse le confondre avec un motif : il n'est lu que par l'affichage.
  const antecedentsFamiliaux = signauxAntecedentsFamiliaux(f);

  const diabete = resoudreDomaine(f.diabeteDiagnostic, FILETS_TEXTE.diabete, texte);
  const digestif = resoudreDomaineMulti(f.digestifDiagnostic, FILETS_TEXTE.digestif, texte);
  const thyroide = resoudreDomaine(f.thyroideDiagnostic, FILETS_TEXTE.thyroide, texte);

  // Statuts conditionnels : ils n'ont de sens que si la pathologie est
  // déclarée. Non renseignés sinon — et surtout jamais déduits.
  // Lecture sur `valeurs` : une MICI déclarée EN PLUS d'un SII ouvre la
  // question du statut exactement comme une MICI déclarée seule.
  digestif.micis = digestif.valeurs.filter((v) => MICI_VALEURS.includes(v));
  digestif.miciDeclaree = digestif.micis.length > 0;
  // 2026-08-02 : combien de MICI pour UN seul statut ? `miciStatut` est un
  // champ unique. Au-dela d'une maladie, il ne peut pas les couvrir toutes.
  digestif.miciPlusieurs = digestif.micis.length > 1;
  // Le statut n'est LU que s'il porte la liaison qui dit à quel ensemble de
  // MICI il répond, et que cette liaison correspond à la sélection présente.
  // Sinon il vaut « non renseigné », donc il bloque.
  //
  // C'est ici que ça se joue, et pas seulement à l'écriture : une fiche
  // écrite avant cette mesure, ou par un chemin qui n'est pas passé par
  // l'assainissement, porte un statut sans liaison. Le résolveur est le
  // dernier à parler avant la décision, c'est donc lui qui doit refuser.
  digestif.statutLie = statutMiciFiable(f);
  digestif.miciStatut = digestif.miciDeclaree && digestif.statutLie
    ? (lc(f.miciStatut) || NON_RENSEIGNE)
    : NON_RENSEIGNE;

  // ── 2026-08-02, arbitrage d'Anissa : UN STATUT PAR MALADIE ─────────────
  //
  // `miciStatuts` porte, maladie par maladie, ce qu'on a le droit de croire.
  // Une maladie déclarée sans statut fiable n'y figure pas : c'est ce qui
  // rend le blocage automatique, sans que ce fichier ait à connaître les
  // règles de liaison.
  //
  // `micisSansStatut` sert au guidage (chantier 5c) et au message : dire
  // « il manque le statut de la rectocolite » vaut mieux que « statut non
  // clarifié », quand la cliente en a déjà renseigné deux sur trois.
  digestif.miciStatuts = statutsMiciFiables(f);
  digestif.micisSansStatut = digestif.micis.filter((m) => !digestif.miciStatuts[m]);
  digestif.toutesEnRemission = toutesMicisEnRemission(f);

  thyroide.stabilisation = THYRO_A_STABILISER.includes(thyroide.valeur)
    ? (lc(f.thyroideStatut) || NON_RENSEIGNE)
    : NON_RENSEIGNE;

  // Traitements. Le statut « vérifié » n'est JAMAIS déduit : il vaut vrai
  // uniquement si Anissa l'a explicitement posé côté praticienne.
  const currentTreatments = readCurrentTreatments(f);
  const traitements = {
    declares: lc(f.traitementsActuels) || NON_RENSEIGNE,
    detail: currentTreatments.text,
    momentPrise: currentTreatments.moment,
    // Champ LEGACY, conservé pour la migration douce. Affiché côté
    // praticienne comme « information précédemment renseignée ». Sa
    // présence ne vaut NI déclaration structurée, NI vérification.
    legacyTexte: String(f.traitements || f.medicaments || '').trim(),
    verifieParAnissa: lc(f.traitementsVerifiesParAnissa) === 'oui',
    verifieLe: String(f.traitementsVerifiesLe ?? '').trim() || null,
  };

  const verificationRequise = [];
  if (diabete.verificationRequise) verificationRequise.push('diabete');
  if (digestif.verificationRequise) verificationRequise.push('digestif');
  if (thyroide.verificationRequise) verificationRequise.push('thyroide');

  const etat = { diabete, digestif, thyroide, traitements, verificationRequise };

  // Les sept situations. `motifs` porte les deux niveaux ; `blocages` ne
  // contient que ceux qui refusent la génération. `bloque` est le booléen
  // unique que consultent les trois chemins.
  const motifs = evaluerSituations(f, etat, texte);
  const blocages = motifs.filter((m) => m.niveau === NIVEAU.BLOCAGE);
  const validationsObligatoires = motifs.filter((m) => m.niveau === NIVEAU.VALIDATION);

  return {
    ...etat,
    motifs,
    blocages,
    validationsObligatoires,
    // Le signal familial voyage SÉPARÉMENT de `motifs`, et c'est tout
    // l'intérêt : il ne peut pas se retrouver dans `blocages` par accident,
    // puisqu'il n'y entre jamais. Il n'est lu que par l'affichage de la fiche.
    antecedentsFamiliaux,
    // Une validation obligatoire empêche AUSSI la génération : on ne produit
    // pas de plan tant que la situation n'est pas clarifiée (consigne Anissa).
    // Le signal familial n'en fait pas partie — décision d'Anissa 2026-08-03.
    bloque: motifs.length > 0,
  };
}

// ─── Les sept blocages ───────────────────────────────────────────────────
//
// Registre validé par Anissa le 2026-07-31
// (docs/MATRICE-SIGNAUX-BLOCAGES-2026-07-31.md). Toute divergence entre ce
// registre et le code doit casser un test.
//
//   1. grossesse en cours
//   2. allaitement en cours
//   3. diabète de type 1
//   4. diabète gestationnel
//   5. MICI active OU statut inconnu
//   6. hyperthyroïdie non stabilisée
//   7. maladie de Basedow non stabilisée
//
// DEUX NIVEAUX, et la distinction vient d'elle :
//   - BLOCAGE : signal structuré. La génération automatique est refusée.
//   - VALIDATION OBLIGATOIRE : situation seulement détectée dans du texte
//     libre. On ne génère pas non plus, mais on ne l'affirme pas — la fiche
//     attend une clarification.
//
// FAIL-CLOSED VOULU : « statut inconnu » bloque. C'est explicitement sa
// décision, ne pas l'assouplir. Une MICI ne repasse qu'en rémission
// confirmée ; une hyperthyroïdie ou un Basedow qu'en stabilisation confirmée.
//
// Les contraintes maternelles de clinicalMaternalSafety.js restent en place
// comme deuxième niveau de protection : elles ne sont PAS remplacées par ces
// blocages (consigne Anissa).

/** Identifiants stables des sept situations. */
export const BLOCAGE = Object.freeze({
  GROSSESSE: 'grossesse',
  ALLAITEMENT: 'allaitement',
  DIABETE_T1: 'diabete_type1',
  DIABETE_GESTATIONNEL: 'diabete_gestationnel',
  MICI: 'mici_active_ou_inconnue',
  HYPERTHYROIDIE: 'hyperthyroidie_non_stabilisee',
  BASEDOW: 'basedow_non_stabilise',
});

/** Niveau d'un motif. */
export const NIVEAU = Object.freeze({
  BLOCAGE: 'blocage',
  VALIDATION: 'validation_obligatoire',
});

/**
 * Situations qui n'ont JAMAIS le niveau blocage : elles suspendent la
 * génération en attendant une clarification, sans rien affirmer.
 *
 * Ajoutées le 2026-08-01 sur les arbitrages 2 et 5 d'Anissa. Elles sont
 * volontairement tenues à l'écart des SEPT blocages, qui restent prioritaires
 * et inchangés : ajouter une situation ici ne doit jamais pouvoir en affaiblir
 * une là-bas.
 */
export const A_CLARIFIER = Object.freeze({
  DIABETE: 'diabete_a_clarifier',
  DIGESTIF: 'digestif_a_clarifier',
  THYROIDE: 'thyroide_a_clarifier',
  TRAITEMENTS_TYPE2: 'diabete_type2_traitements_a_verifier',
});

/**
 * Messages affichés à Anissa. Textes VERBATIM validés par elle le
 * 2026-07-31 — ne pas reformuler, ne pas résumer, ne pas traduire.
 */
const MESSAGES = Object.freeze({
  [BLOCAGE.GROSSESSE]:
    'Génération automatique bloquée — grossesse en cours. Vérifiez le trimestre, '
    + 'le suivi médical, les éventuelles complications et le contexte nutritionnel '
    + 'avant de construire le plan manuellement.',
  [BLOCAGE.ALLAITEMENT]:
    'Génération automatique bloquée — allaitement en cours. Vérifiez le caractère '
    + 'exclusif ou partiel de l\'allaitement, la récupération post-partum, la '
    + 'situation du nourrisson et le suivi médical avant de construire le plan '
    + 'manuellement.',
  [BLOCAGE.DIABETE_T1]:
    'Génération automatique bloquée — diabète de type 1. Le plan doit être '
    + 'construit et validé manuellement, en coordination avec l\'équipe médicale, '
    + 'en raison notamment du risque d\'hypoglycémie et de la gestion de l\'insuline.',
  [BLOCAGE.DIABETE_GESTATIONNEL]:
    'Génération automatique bloquée — diabète gestationnel déclaré ou détecté. '
    + 'Une coordination avec le suivi médical est nécessaire avant la création du '
    + 'plan. Vérifiez la situation et validez manuellement la poursuite.',
  [BLOCAGE.MICI]:
    'Génération automatique bloquée — MICI active ou statut non clarifié. Une '
    + 'évaluation médicale et la confirmation du cadre d\'accompagnement sont '
    + 'nécessaires avant la création d\'un plan nutritionnel.',
  // 2026-07-31 — formulations fournies et validées par Anissa. Elles
  // remplacent un texte intermédiaire qui reprenait sa phrase de règle et
  // contenait une INVERSION DE SENS : « une situation stabilisée ne peut
  // être accompagnée qu'après confirmation », alors que c'est la situation
  // NON stabilisée qui bloque. Ne pas reformuler.
  [BLOCAGE.HYPERTHYROIDIE]:
    'Génération automatique bloquée — hyperthyroïdie non stabilisée. Un '
    + 'accompagnement ne peut être envisagé qu\'après confirmation de la '
    + 'stabilisation par le médecin qui assure le suivi.',
  [BLOCAGE.BASEDOW]:
    'Génération automatique bloquée — maladie de Basedow non stabilisée. Un '
    + 'accompagnement ne peut être envisagé qu\'après confirmation de la '
    + 'stabilisation par le médecin qui assure le suivi.',
});

// ─── Messages de VALIDATION OBLIGATOIRE ─────────────────────────────────
//
// Anissa, 2026-07-31 : « Dans le cas d'une détection en texte libre, le
// message rouge affirme actuellement "diabète de type 1". Or, à ce stade,
// il ne s'agit que d'un signal à clarifier. »
//
// Un message de blocage NOMME une situation établie. Un message de
// validation décrit une MENTION. Les deux ne peuvent pas partager le même
// texte : c'est la différence entre constater et suspecter.

/** Forme indéfinie de la situation, pour « une mention pouvant correspondre à … ». */
const LIBELLE_INDEFINI = Object.freeze({
  [BLOCAGE.DIABETE_T1]: 'un diabète de type 1',
  [BLOCAGE.DIABETE_GESTATIONNEL]: 'un diabète gestationnel',
});

/**
 * Phrase validée par Anissa pour le diabète de type 1. La seule variable est
 * le nom de la situation ; le reste est repris mot pour mot.
 */
function messageValidation(id) {
  return `Une mention pouvant correspondre à ${LIBELLE_INDEFINI[id]} a été `
    + 'détectée dans le texte libre. La génération automatique reste suspendue '
    + 'jusqu\'à clarification dans les champs structurés. Aucun diagnostic ni '
    + 'statut clinique n\'est déduit automatiquement.';
}

/**
 * Phrases VALIDÉES par Anissa le 2026-08-01. Ne pas reformuler.
 *
 * Elle a validé trois ouvertures, chacune suivie des deux phrases de
 * validation déjà approuvées le 2026-07-31 :
 *
 *   - cause « réponse incertaine », donnée sur le diabète ;
 *   - cause « texte libre seul », donnée sur la thyroïde ;
 *   - cas du diabète de type 2 et de ses traitements.
 *
 * Les deux premières sont des MODÈLES : elles décrivent une cause, pas un
 * domaine. Le nom du domaine est la seule variable, exactement comme pour
 * `messageValidation()` qu'elle avait validé sur le même principe. Les
 * formes exactes qu'elle a écrites (« le diabète », « à la thyroïde ») sont
 * verrouillées par un test.
 *
 * Consigne associée : ces messages ne déclenchent AUCUNE qualification
 * clinique automatique. Ils n'écrivent rien dans l'état résolu.
 */
const SUITE_VALIDATION =
  'La génération automatique reste suspendue jusqu\'à clarification dans les '
  + 'champs structurés. Aucun diagnostic ni statut clinique n\'est déduit '
  + 'automatiquement.';

/** Le domaine après « concernant ». */
const DOMAINE_CONCERNANT = Object.freeze({
  [A_CLARIFIER.DIABETE]: 'le diabète',
  [A_CLARIFIER.DIGESTIF]: 'la situation digestive',
  [A_CLARIFIER.THYROIDE]: 'la thyroïde',
});

/** Le domaine après « relative ». */
const DOMAINE_RELATIVE_A = Object.freeze({
  [A_CLARIFIER.DIABETE]: 'au diabète',
  [A_CLARIFIER.DIGESTIF]: 'à la situation digestive',
  [A_CLARIFIER.THYROIDE]: 'à la thyroïde',
});

/** Les deux causes possibles d'une clarification de domaine. */
export const CAUSE = Object.freeze({
  REPONSE_INCERTAINE: 'reponse_incertaine',
  TEXTE_LIBRE_SEUL: 'texte_libre_seul',
});

function messageAClarifier(id, cause) {
  if (id === A_CLARIFIER.TRAITEMENTS_TYPE2) {
    return 'Un diabète de type 2 est déclaré, mais les informations relatives '
      + 'aux traitements ne sont pas encore suffisamment renseignées ou '
      + `vérifiées par Anissa. ${SUITE_VALIDATION}`;
  }
  if (cause === CAUSE.REPONSE_INCERTAINE) {
    return `La réponse concernant ${DOMAINE_CONCERNANT[id]} ne permet pas `
      + `d'identifier précisément la situation déclarée. ${SUITE_VALIDATION}`;
  }
  return `Une mention relative ${DOMAINE_RELATIVE_A[id]} a été détectée `
    + 'uniquement dans le texte libre, sans réponse structurée '
    + `correspondante. ${SUITE_VALIDATION}`;
}

/** Libellés courts, pour lister plusieurs motifs séparément. */
const LIBELLES = Object.freeze({
  [A_CLARIFIER.DIABETE]: 'Diabète à clarifier',
  [A_CLARIFIER.DIGESTIF]: 'Situation digestive à clarifier',
  [A_CLARIFIER.THYROIDE]: 'Thyroïde à clarifier',
  [A_CLARIFIER.TRAITEMENTS_TYPE2]: 'Traitements à vérifier (diabète de type 2)',
  [BLOCAGE.GROSSESSE]: 'Grossesse en cours',
  [BLOCAGE.ALLAITEMENT]: 'Allaitement en cours',
  [BLOCAGE.DIABETE_T1]: 'Diabète de type 1',
  [BLOCAGE.DIABETE_GESTATIONNEL]: 'Diabète gestationnel',
  [BLOCAGE.MICI]: 'MICI active ou statut non clarifié',
  [BLOCAGE.HYPERTHYROIDIE]: 'Hyperthyroïdie non stabilisée',
  [BLOCAGE.BASEDOW]: 'Maladie de Basedow non stabilisée',
});

/**
 * Filets texte SPÉCIFIQUES aux situations bloquantes. Volontairement plus
 * étroits que FILETS_TEXTE : celui-ci sert à demander une vérification, ceux
 * -ci à refuser une génération. « reflux » ne doit pas bloquer une cliente.
 */
const FILETS_SITUATION = Object.freeze({
  [BLOCAGE.DIABETE_T1]:
    /diab[eè]te\s+(?:de\s+)?type\s*1|diab[eè]te\s+insulino|\bdt1\b|insulinod[eé]pendant/i,
  [BLOCAGE.DIABETE_GESTATIONNEL]: /diab[eè]te\s+gestationnel|gestational\s+diabetes/i,
  [BLOCAGE.MICI]: /\bmici\b|crohn|rectocolite|\brch\b/i,
});

/**
 * Un motif porte le message qui correspond à SON niveau : constat pour un
 * blocage, mention à clarifier pour une validation obligatoire.
 */
const motif = (id, niveau, source) => ({
  id,
  niveau,
  libelle: LIBELLES[id],
  message: niveau === NIVEAU.VALIDATION ? messageValidation(id) : MESSAGES[id],
  source,
});

/**
 * Évalue les sept situations. Ne dépend QUE de l'état déjà résolu : le champ
 * structuré prime, le texte libre n'intervient que là où rien n'est renseigné.
 */
function evaluerSituations(form, etat, texte) {
  const motifs = [];

  // 1 & 2 — États maternels. Signal structuré depuis grossesseActuelle et les
  // champs legacy, filet texte inclus (resolveMaternalStateExtended).
  const maternel = resolveMaternalStateExtended(form);
  if (maternel === 'grossesse') motifs.push(motif(BLOCAGE.GROSSESSE, NIVEAU.BLOCAGE, 'structure'));
  if (maternel === 'allaitement') motifs.push(motif(BLOCAGE.ALLAITEMENT, NIVEAU.BLOCAGE, 'structure'));

  // 3 & 4 — Diabète. Structuré → blocage ; texte libre seul → validation.
  if (etat.diabete.valeur === 'type1') {
    motifs.push(motif(BLOCAGE.DIABETE_T1, NIVEAU.BLOCAGE, 'structure'));
  } else if (etat.diabete.source === SOURCE.ABSENT && FILETS_SITUATION[BLOCAGE.DIABETE_T1].test(texte)) {
    motifs.push(motif(BLOCAGE.DIABETE_T1, NIVEAU.VALIDATION, 'texte'));
  }

  if (etat.diabete.valeur === 'gestationnel') {
    motifs.push(motif(BLOCAGE.DIABETE_GESTATIONNEL, NIVEAU.BLOCAGE, 'structure'));
  } else if (
    etat.diabete.source === SOURCE.ABSENT
    && FILETS_SITUATION[BLOCAGE.DIABETE_GESTATIONNEL].test(texte)
  ) {
    motifs.push(motif(BLOCAGE.DIABETE_GESTATIONNEL, NIVEAU.VALIDATION, 'texte'));
  }

  // 5 — MICI. Seule une rémission confirmée passe : actif, « je ne sais pas »
  // et statut absent bloquent tous les trois (fail-closed voulu).
  //
  // MESURE PROVISOIRE DU 2026-08-02 : plusieurs MICI pour un seul statut ne
  // valent pas confirmation. `miciStatut` est un champ unique ; une
  // rémission confirmée pour Crohn ne dit rien d'une seconde maladie, qui
  // n'a jamais été évaluée. Sans cette ligne, cocher « autre MICI » après
  // avoir répondu « en rémission » pour Crohn levait le blocage pour les
  // deux. C'était un fail-open dans une règle écrite pour être fail-closed.
  //
  // Provisoire jusqu'à l'arbitrage d'Anissa : statut global, ou statut par
  // maladie ? Tant que ce n'est pas tranché, on bloque. Voir
  // docs/MICI-STATUT-UNIQUE-2026-08-02.md.
  if (etat.digestif.miciDeclaree) {
    // 2026-08-02, arbitrage d'Anissa (option A) : CHAQUE maladie déclarée
    // doit porter sa propre rémission confirmée. La mesure du matin bloquait
    // dès qu'il y avait plusieurs MICI, faute de savoir laquelle le statut
    // unique décrivait ; on sait désormais le dire maladie par maladie, donc
    // on ne bloque plus que sur ce qui manque VRAIMENT.
    //
    // Le sens de la règle est inchangé : seule une rémission confirmée
    // passe. Elle s'applique maintenant à chacune au lieu de l'ensemble.
    if (!etat.digestif.toutesEnRemission) {
      motifs.push(motif(BLOCAGE.MICI, NIVEAU.BLOCAGE, 'structure'));
    }
  } else if (
    etat.digestif.source === SOURCE.ABSENT
    && FILETS_SITUATION[BLOCAGE.MICI].test(texte)
  ) {
    // Une MICI evoquée sans rien de structuré : statut inconnu par
    // construction. Elle a demandé que ce cas bloque aussi.
    motifs.push(motif(BLOCAGE.MICI, NIVEAU.BLOCAGE, 'texte'));
  }

  // 6 & 7 — Thyroïde. Seule une stabilisation confirmée passe.
  if (etat.thyroide.valeur === 'hyperthyroidie' && etat.thyroide.stabilisation !== 'oui') {
    motifs.push(motif(BLOCAGE.HYPERTHYROIDIE, NIVEAU.BLOCAGE, 'structure'));
  }
  if (etat.thyroide.valeur === 'basedow' && etat.thyroide.stabilisation !== 'oui') {
    motifs.push(motif(BLOCAGE.BASEDOW, NIVEAU.BLOCAGE, 'structure'));
  }

  // ─── Arbitrages 2 et 5 (2026-08-01) ───────────────────────────────────
  // Ajoutés APRÈS les sept, et jamais au niveau blocage. Un domaine déjà
  // porteur d'un motif n'en reçoit pas un second : une cliente dont la MICI
  // bloque déjà n'a pas besoin qu'on lui dise en plus que son digestif est
  // à clarifier.
  motifs.push(...evaluerClarifications(etat, motifs));

  return motifs;
}

/** Domaine auquel se rattache chacune des sept situations. */
const DOMAINE_DU_BLOCAGE = Object.freeze({
  [BLOCAGE.DIABETE_T1]: 'diabete',
  [BLOCAGE.DIABETE_GESTATIONNEL]: 'diabete',
  [BLOCAGE.MICI]: 'digestif',
  [BLOCAGE.HYPERTHYROIDIE]: 'thyroide',
  [BLOCAGE.BASEDOW]: 'thyroide',
});

const motifClarification = (id, cause, source) => ({
  id,
  niveau: NIVEAU.VALIDATION,
  libelle: LIBELLES[id],
  message: messageAClarifier(id, cause),
  cause,
  source,
});

/**
 * Arbitrage 2 : « Autre », « je ne sais pas », ou une information présente
 * UNIQUEMENT dans le texte libre entraînent une validation obligatoire.
 * Aucun sous-module ne s'active automatiquement dans ces cas.
 *
 * Arbitrage 5 : le diabète de type 2 n'est admissible sans validation que si
 * AUCUN traitement n'est déclaré. Dès qu'un traitement est déclaré, non
 * renseigné ou non vérifié, la fiche passe en validation.
 */
function evaluerClarifications(etat, dejaPresents) {
  const sortie = [];
  const domainesCouverts = new Set(
    dejaPresents.map((m) => DOMAINE_DU_BLOCAGE[m.id]).filter(Boolean),
  );

  const domaines = [
    { cle: 'diabete', id: A_CLARIFIER.DIABETE, etat: etat.diabete },
    { cle: 'digestif', id: A_CLARIFIER.DIGESTIF, etat: etat.digestif },
    { cle: 'thyroide', id: A_CLARIFIER.THYROIDE, etat: etat.thyroide },
  ];

  for (const d of domaines) {
    if (domainesCouverts.has(d.cle)) continue;

    const valeurs = d.etat.valeurs || [d.etat.valeur];
    const incertaine = valeurs.some((v) => REPONSES_INCERTAINES.includes(v));

    if (incertaine) {
      sortie.push(motifClarification(d.id, CAUSE.REPONSE_INCERTAINE, 'structure'));
    } else if (d.etat.verificationRequise) {
      sortie.push(motifClarification(d.id, CAUSE.TEXTE_LIBRE_SEUL, 'texte'));
    }
  }

  // Arbitrage 5. La case de vérification est réservée à la praticienne et ne
  // lève JAMAIS un autre blocage clinique : elle n'intervient qu'ici.
  if (etat.diabete.valeur === 'type2') {
    const aucunTraitement = etat.traitements.declares === 'non';
    const traitementDeclareComplet = etat.traitements.declares === 'oui'
      && Boolean(etat.traitements.detail);
    const verifie = etat.traitements.verifieParAnissa === true
      && (aucunTraitement || traitementDeclareComplet);
    if (!aucunTraitement && !verifie) {
      sortie.push(motifClarification(
        A_CLARIFIER.TRAITEMENTS_TYPE2, CAUSE.REPONSE_INCERTAINE, 'structure',
      ));
    }
  }

  return sortie;
}

/**
 * Rend les motifs lisibles pour la praticienne. Chaque motif est listé
 * SÉPARÉMENT (consigne Anissa) : trois situations bloquantes doivent
 * produire trois lignes, pas un message fourre-tout.
 */
export function formatBlocages(motifs) {
  const liste = motifs || [];
  if (!liste.length) return '';
  const titre = 'Génération automatique bloquée';
  // Le message de chaque motif est repris tel quel : celui d'une validation
  // obligatoire dit déjà qu'il s'agit d'une mention détectée, inutile de le
  // redire dans une étiquette.
  const lignes = liste.map((m) => `• ${m.message}`);
  return `${titre}\n\n${lignes.join('\n\n')}\n\n${noteFinale(liste)}`;
}

/**
 * Note de clôture. Elle DÉPEND du niveau réel des motifs.
 *
 * Anissa, 2026-07-31 : « la phrase commune indique que le plan doit être
 * construit manuellement. Ce n'est pas nécessairement vrai. Après
 * clarification dans les champs structurés, la mention peut être infirmée et
 * la génération automatique redevenir possible. »
 *
 * Une détection en texte libre est une porte fermée en attendant, pas une
 * porte condamnée. La phrase sur la construction manuelle reste réservée aux
 * situations structurées, qui constituent réellement un blocage.
 */
export function noteFinale(motifs) {
  const liste = motifs || [];
  const aUnBlocageReel = liste.some((m) => m.niveau === NIVEAU.BLOCAGE);
  if (aUnBlocageReel) {
    return 'Le plan doit être construit manuellement après vérification du contexte '
      + 'et, lorsque nécessaire, coordination avec le suivi médical.';
  }
  return 'La génération automatique reste suspendue jusqu\'à clarification dans '
    + 'les champs structurés. La case de vérification ci-dessous ne remplace pas '
    + 'cette clarification.';
}

/** Libellés des domaines, pour le message de vérification. */
const LIBELLE_DOMAINE = Object.freeze({
  diabete: 'un diabète',
  digestif: 'un trouble digestif',
  thyroide: 'un trouble thyroïdien',
});

/**
 * Message affiché à Anissa pour un dossier existant sans champs structurés.
 * Texte validé par elle le 2026-07-31 — ne pas reformuler. Le domaine
 * détecté est précisé dynamiquement, sans jamais présenter la détection
 * comme un diagnostic.
 */
export function messageVerificationClinique(domaines) {
  const liste = (domaines || [])
    .map((d) => LIBELLE_DOMAINE[d])
    .filter(Boolean);
  const mention = liste.length
    ? liste.length === 1
      ? liste[0]
      : `${liste.slice(0, -1).join(', ')} ou ${liste[liste.length - 1]}`
    : 'un diabète, un trouble digestif ou un trouble thyroïdien';

  return {
    titre: 'Vérification clinique requise',
    corps:
      `Ce dossier contient une mention pouvant correspondre à ${mention}, `
      + 'mais aucune réponse structurée ne permet actuellement de la confirmer. '
      + 'Complétez la section « Santé et suivi médical » avant toute nouvelle '
      + 'génération. Aucun diagnostic ni statut clinique n\'est déduit '
      + 'automatiquement du texte libre.',
  };
}
